import { useState } from "react";
import type { useAccount } from "../../controllers/useAccount";
import type { AccountType, CompanyProfile, CompanySize, Gender, IndividualProfile } from "../../models/account";

// First-login onboarding gate. The account row does NOT exist yet — /api/account/me returned a
// token-seeded prefill (onboarded=false). Here the user PICKS the account type, confirms their email,
// and fills in the profile our system needs; on submit the account is created (deferred provisioning)
// and the shell unlocks.

const GENDER: { v: Gender; label: string }[] = [
  { v: "UNDISCLOSED", label: "밝히지 않음" },
  { v: "MALE", label: "남성" },
  { v: "FEMALE", label: "여성" },
  { v: "OTHER", label: "기타" },
];
const COMPANY_SIZE: { v: CompanySize; label: string }[] = [
  { v: "UNKNOWN", label: "선택 안 함" },
  { v: "SOLO", label: "1인" },
  { v: "SMALL", label: "2-49인" },
  { v: "MEDIUM", label: "50-249인" },
  { v: "LARGE", label: "250-999인" },
  { v: "ENTERPRISE", label: "1000인 이상" },
];

export function Onboarding({ acc, isAdmin = false }: { acc: ReturnType<typeof useAccount>; isAdmin?: boolean }) {
  const prefill = acc.account;

  // Admins onboard as a fixed "관리자 계정" (INDIVIDUAL under the hood) — no personal/company choice.
  const [accountType, setAccountType] = useState<AccountType>(isAdmin ? "INDIVIDUAL" : (prefill?.accountType ?? "INDIVIDUAL"));
  const [email, setEmail] = useState(prefill?.email ?? "");
  const [displayName, setDisplayName] = useState(prefill?.displayName ?? "");
  const [phone, setPhone] = useState(prefill?.phone ?? "");
  const [ind, setInd] = useState<IndividualProfile>({});
  const [co, setCo] = useState<CompanyProfile>({});
  const [agree, setAgree] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setI = <K extends keyof IndividualProfile>(k: K, v: IndividualProfile[K]) =>
    setInd((f) => ({ ...f, [k]: v }));
  const setC = <K extends keyof CompanyProfile>(k: K, v: CompanyProfile[K]) =>
    setCo((f) => ({ ...f, [k]: v }));

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const nameOk = displayName.trim().length > 0;
  const companyOk = accountType !== "COMPANY" || (co.legalName ?? "").trim().length > 0;
  const canSubmit = emailOk && nameOk && companyOk && agree && !busy;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await acc.onboard({
        accountType,
        email: email.trim(),
        displayName: displayName.trim(),
        phone: phone.trim() || null,
        individualProfile: accountType === "INDIVIDUAL" ? ind : null,
        companyProfile: accountType === "COMPANY" ? co : null,
        agreeTerms: true,
        marketingOptIn: marketing,
      });
      // On success the account is created + onboarded; App re-renders into the shell.
    } catch (e) {
      setErr(e instanceof Error ? e.message : "저장에 실패했습니다. 다시 시도해 주세요.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="onboarding">
      <div className="onboarding-card info-card">
        <h1 style={{ marginTop: 0 }}>compath 시작하기</h1>
        <p className="sdui-help" style={{ marginTop: 4 }}>
          계정 정보를 입력해 주세요. 입력을 마쳐야 계정이 생성됩니다. 나중에 &lsquo;내 계정&rsquo;에서 언제든 수정할 수 있습니다.
        </p>

        {isAdmin ? (
          <div className="onb-type">
            <div className="onb-type-opt on" aria-label="관리자 계정" style={{ flex: 1 }}>
              <span className="onb-type-title">관리자 계정</span>
              <span className="onb-type-desc">플랫폼 관리자로 등록됩니다</span>
            </div>
          </div>
        ) : (
          /* Account type — the user's choice, drives which profile is required. */
          <div className="onb-type" role="radiogroup" aria-label="계정 유형">
            <button
              type="button"
              className={`onb-type-opt ${accountType === "INDIVIDUAL" ? "on" : ""}`}
              aria-pressed={accountType === "INDIVIDUAL"}
              onClick={() => setAccountType("INDIVIDUAL")}
            >
              <span className="onb-type-title">개인 계정</span>
              <span className="onb-type-desc">개인 사용자로 시작</span>
            </button>
            <button
              type="button"
              className={`onb-type-opt ${accountType === "COMPANY" ? "on" : ""}`}
              aria-pressed={accountType === "COMPANY"}
              onClick={() => setAccountType("COMPANY")}
            >
              <span className="onb-type-title">기업 계정</span>
              <span className="onb-type-desc">회사/팀으로 시작</span>
            </button>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 18 }}>
          <label className="sdui-field">
            <span className="sdui-label">이메일 *</span>
            <input className="sdui-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            {email.length > 0 && !emailOk && <span className="onb-err">올바른 이메일 형식이 아닙니다.</span>}
          </label>
          <div style={{ display: "flex", gap: 14 }}>
            <label className="sdui-field" style={{ flex: 1 }}>
              <span className="sdui-label">{accountType === "COMPANY" ? "담당자 표시 이름 *" : "표시 이름 *"}</span>
              <input className="sdui-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
            <label className="sdui-field" style={{ flex: 1 }}>
              <span className="sdui-label">전화번호</span>
              <input className="sdui-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>
          </div>

          {accountType === "INDIVIDUAL" ? (
            <>
              <div style={{ display: "flex", gap: 14 }}>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">성</span>
                  <input className="sdui-input" value={ind.familyName ?? ""} onChange={(e) => setI("familyName", e.target.value)} />
                </label>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">이름</span>
                  <input className="sdui-input" value={ind.givenName ?? ""} onChange={(e) => setI("givenName", e.target.value)} />
                </label>
              </div>
              <div style={{ display: "flex", gap: 14 }}>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">성별</span>
                  <select className="sdui-input" value={ind.gender ?? "UNDISCLOSED"} onChange={(e) => setI("gender", e.target.value as Gender)}>
                    {GENDER.map((g) => <option key={g.v} value={g.v}>{g.label}</option>)}
                  </select>
                </label>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">생년월일</span>
                  <input className="sdui-input" type="date" value={ind.birthDate ?? ""} onChange={(e) => setI("birthDate", e.target.value)} />
                </label>
              </div>
              <div style={{ display: "flex", gap: 14 }}>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">직함</span>
                  <input className="sdui-input" value={ind.jobTitle ?? ""} onChange={(e) => setI("jobTitle", e.target.value)} />
                </label>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">소속</span>
                  <input className="sdui-input" value={ind.organization ?? ""} onChange={(e) => setI("organization", e.target.value)} />
                </label>
              </div>
            </>
          ) : (
            <>
              <div style={{ display: "flex", gap: 14 }}>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">법인명 *</span>
                  <input className="sdui-input" value={co.legalName ?? ""} onChange={(e) => setC("legalName", e.target.value)} />
                  {(co.legalName ?? "").length === 0 && <span className="onb-err">기업 계정은 법인명이 필요합니다.</span>}
                </label>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">브랜드명</span>
                  <input className="sdui-input" value={co.brandName ?? ""} onChange={(e) => setC("brandName", e.target.value)} />
                </label>
              </div>
              <div style={{ display: "flex", gap: 14 }}>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">사업자등록번호</span>
                  <input className="sdui-input" value={co.registrationNumber ?? ""} onChange={(e) => setC("registrationNumber", e.target.value)} />
                </label>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">업종</span>
                  <input className="sdui-input" value={co.industry ?? ""} onChange={(e) => setC("industry", e.target.value)} />
                </label>
              </div>
              <div style={{ display: "flex", gap: 14 }}>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">기업 규모</span>
                  <select className="sdui-input" value={co.companySize ?? "UNKNOWN"} onChange={(e) => setC("companySize", e.target.value as CompanySize)}>
                    {COMPANY_SIZE.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </label>
                <label className="sdui-field" style={{ flex: 1 }}>
                  <span className="sdui-label">담당자 이메일</span>
                  <input className="sdui-input" value={co.contactEmail ?? ""} onChange={(e) => setC("contactEmail", e.target.value)} />
                </label>
              </div>
              <label className="sdui-field">
                <span className="sdui-label">본사 주소</span>
                <input className="sdui-input" value={co.hqAddressLine1 ?? ""} onChange={(e) => setC("hqAddressLine1", e.target.value)} />
              </label>
            </>
          )}

          <label className="onboarding-terms">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            <span>서비스 이용약관 및 개인정보 처리방침에 동의합니다. *</span>
          </label>
          <label className="onboarding-terms">
            <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} />
            <span>마케팅 정보 수신에 동의합니다. (선택)</span>
          </label>

          {err && (
            <div className="note" style={{ background: "#fdeef0" }}>
              <b style={{ color: "var(--amber)" }}>오류:</b> {err}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="btn primary" style={{ width: "auto" }} disabled={!canSubmit} onClick={() => void submit()}>
              {busy ? "계정 생성 중…" : "시작하기"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
